import { useState } from "react";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import {
  Dialog,
  DialogContent,
} from "../components/ui/dialog";
import { Button } from "../components/ui/button";
import {
  Sparkles, ShieldCheck, Megaphone, Palette, Clapperboard, ArrowRight, ArrowLeft, Check,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * OnboardingPopup — multi-step tour shown to first-time users.
 *
 *   Step 0  Safety notice (zero-tolerance policy, same copy as Terms §8.1).
 *   Step 1  Marketing mode (coral wash).
 *   Step 2  Designer mode  (violet wash).
 *   Step 3  CGI mode       (blue wash).
 *   Step 4  Auto-import-products choice (the legacy first-time question).
 *
 * Each mode step renders a large icon, label, tagline, and 3 feature bullets.
 * The background is washed with the mode's accent color in two soft animated
 * blobs that drift slowly — "lively" as the user requested. We use CSS
 * `animate-pulse` plus a translate keyframe defined inline so we don't need
 * to add anything to tailwind.config.
 */
const MODE_STEPS = [
  {
    id: "marketing",
    Icon: Megaphone,
    label: "Marketing",
    tagline: "AI-powered creatives for your store",
    accent: "#FF4F27",
    bullets: [
      "Generate scroll-stopping IG / TikTok creatives from your product catalogue.",
      "Bulk-publish across platforms with the right format and copy for each.",
      "AI Video & Reels engine batches polished clips with music, captions, and brand frames.",
    ],
  },
  {
    id: "designer",
    Icon: Palette,
    label: "Designing",
    tagline: "Pure creative play, no store needed",
    accent: "#8B5CF6",
    bullets: [
      "Sketch ideas with the Designer chat — no product catalogue required.",
      "Generate stylized images and reference boards for any project.",
      "Save to your gallery, share to the community, remix anytime.",
    ],
  },
  {
    id: "cgi",
    Icon: Clapperboard,
    label: "CGI",
    tagline: "Movie-grade effects on your generations",
    accent: "#3B82F6",
    bullets: [
      "FX Lab applies AI re-lighting, frame stylize, and filter overlays on your renders.",
      "Movie Mode generates full short films from nothing with multi-shot character consistency.",
      "Timeline & Composer let you cut, stitch, and polish multi-clip cinematic edits.",
    ],
  },
];

const OnboardingPopup = () => {
  const { user, refreshUser } = useAuth();
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);

  // Show popup only if user exists and hasn't completed onboarding.
  const showPopup = user && user.auto_import_products === null && !user.onboarding_completed;
  if (!showPopup) return null;

  const totalSteps = 1 /* safety */ + MODE_STEPS.length + 1 /* product import */;

  // Step 0 = safety, steps 1..3 = modes, step 4 = final product-import choice.
  const isSafety  = step === 0;
  const isFinal   = step === totalSteps - 1;
  const modeIdx   = step - 1;
  const modeStep  = !isSafety && !isFinal ? MODE_STEPS[modeIdx] : null;

  const handleChoice = async (autoImport) => {
    setSaving(true);
    try {
      await axios.patch(`${API}/auth/preferences`, { auto_import_products: autoImport });
      await refreshUser();
    } catch (e) {
      // surface to console but don't block — user can change in Settings later.
      console.error("Failed to save preference:", e);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={showPopup}>
      <DialogContent
        className="bg-white border-ink-200 max-w-2xl p-0 overflow-hidden"
        hideCloseButton
        data-testid="onboarding-popup"
      >
        {/* Animated background shades — only on mode steps. Two big blurred
            blobs that drift on a slow loop so the panel feels alive without
            being distracting. */}
        {modeStep && (
          <>
            <style>{`
              @keyframes onboardingBlobA {
                0%, 100% { transform: translate(-15%, -10%) scale(1); }
                50%      { transform: translate( 15%,  10%) scale(1.15); }
              }
              @keyframes onboardingBlobB {
                0%, 100% { transform: translate( 20%,  15%) scale(1); }
                50%      { transform: translate(-10%, -15%) scale(1.2); }
              }
              .onboarding-blob-a { animation: onboardingBlobA 12s ease-in-out infinite; }
              .onboarding-blob-b { animation: onboardingBlobB 16s ease-in-out infinite; }
            `}</style>
            <div
              aria-hidden="true"
              data-testid={`onboarding-shade-${modeStep.id}`}
              className="absolute inset-0 overflow-hidden pointer-events-none"
            >
              <div
                className="onboarding-blob-a absolute -top-24 -left-24 w-[55%] h-[80%] rounded-full blur-3xl opacity-40"
                style={{ background: modeStep.accent }}
              />
              <div
                className="onboarding-blob-b absolute -bottom-24 -right-24 w-[55%] h-[80%] rounded-full blur-3xl opacity-30"
                style={{ background: modeStep.accent }}
              />
            </div>
          </>
        )}

        {/* Content sits on top of the shades */}
        <div className="relative z-10 p-7">
          {isSafety && (
            <SafetyStep onNext={() => setStep(1)} />
          )}

          {modeStep && (
            <ModeStep step={modeStep} index={modeIdx + 1} total={MODE_STEPS.length} />
          )}

          {isFinal && (
            <ProductChoiceStep onChoose={handleChoice} saving={saving} />
          )}

          {/* Footer nav — Back / Next / Step dots */}
          {!isFinal && (
            <div className="mt-7 flex items-center justify-between gap-3">
              <Button
                variant="outline"
                onClick={() => setStep((s) => Math.max(0, s - 1))}
                disabled={step === 0}
                size="sm"
                className="border-ink-200"
                data-testid="onboarding-back-btn"
              >
                <ArrowLeft className="w-3.5 h-3.5 mr-1" /> Back
              </Button>

              <StepDots count={totalSteps} current={step} />

              <Button
                onClick={() => setStep((s) => Math.min(totalSteps - 1, s + 1))}
                size="sm"
                className="bg-ink-950 hover:bg-ink-900 text-white"
                data-testid="onboarding-next-btn"
              >
                Next <ArrowRight className="w-3.5 h-3.5 ml-1" />
              </Button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

// =====================================================================
// Sub-views
// =====================================================================
const SafetyStep = ({ onNext }) => (
  <div data-testid="onboarding-safety-step">
    <h2 className="text-2xl font-black text-ink-950 text-center mb-1">
      Welcome to XEA
    </h2>
    <p className="text-sm text-ink-500 text-center mb-6">
      A quick tour of the three modes you can switch between any time.
    </p>

    <div
      className="mb-2 p-4 rounded-2xl bg-red-500/10 border-2 border-red-500/40 flex items-start gap-3"
      data-testid="onboarding-safety-notice"
    >
      <ShieldCheck className="w-6 h-6 text-red-600 shrink-0" />
      <div>
        <p className="text-red-700 font-bold">Zero-Tolerance Safety Policy</p>
        <p className="text-sm text-ink-700 mt-1">
          Any sexual-content generation attempt — in <em>any</em> studio
          (Marketing, Designing, CGI) and through <em>any</em> field
          (prompt, title, text overlay, caption) — triggers an{" "}
          <strong>immediate permanent ban</strong>. No warning, no exception,
          no appeal. We protect children.
        </p>
      </div>
    </div>
  </div>
);

const ModeStep = ({ step, index, total }) => {
  const { Icon } = step;
  return (
    <div data-testid={`onboarding-mode-step-${step.id}`}>
      <p className="text-[10px] uppercase tracking-[0.22em] font-bold text-ink-500 text-center">
        Mode {index} of {total}
      </p>

      <div className="flex flex-col items-center text-center mt-3 mb-5">
        <div
          className="w-16 h-16 rounded-2xl flex items-center justify-center shadow-lg mb-3"
          style={{
            background: `linear-gradient(135deg, ${step.accent}, ${step.accent}cc)`,
          }}
        >
          <Icon className="w-8 h-8 text-white" />
        </div>
        <h2 className="text-3xl font-black text-ink-950 tracking-tight">
          {step.label}
        </h2>
        <p className="text-sm text-ink-700 mt-1 italic">{step.tagline}</p>
      </div>

      <ul className="space-y-2.5 max-w-md mx-auto">
        {step.bullets.map((b, i) => (
          <li key={i} className="flex items-start gap-2.5">
            <div
              className="shrink-0 w-5 h-5 rounded-full flex items-center justify-center mt-0.5"
              style={{ background: step.accent }}
            >
              <Check className="w-3 h-3 text-white" />
            </div>
            <p className="text-sm text-ink-800 leading-relaxed">{b}</p>
          </li>
        ))}
      </ul>
    </div>
  );
};

const ProductChoiceStep = ({ onChoose, saving }) => (
  <div data-testid="onboarding-product-choice-step">
    <div className="text-center mb-6">
      <div className="w-16 h-16 rounded-2xl bg-coral-500/20 flex items-center justify-center mx-auto mb-4">
        <Sparkles className="w-8 h-8 text-coral-500" />
      </div>
      <h2 className="text-2xl font-black text-ink-950">One last thing</h2>
      <p className="text-sm text-ink-500 mt-1">
        How would you like to add products? You can change this later in Settings.
      </p>
    </div>

    <div className="space-y-3">
      <Button
        onClick={() => onChoose(true)}
        disabled={saving}
        className="w-full h-auto py-4 bg-coral-500 hover:bg-coral-600 flex-col items-start"
        data-testid="auto-import-yes"
      >
        <div className="flex items-center gap-3 w-full">
          <Sparkles className="w-6 h-6 text-violet-200" />
          <div className="text-left">
            <p className="font-semibold">Auto-Import Products</p>
            <p className="text-sm text-violet-200/80 font-normal">
              {`Connect your store URL and we'll import products automatically`}
            </p>
          </div>
        </div>
      </Button>

      <Button
        onClick={() => onChoose(false)}
        disabled={saving}
        variant="outline"
        className="w-full h-auto py-4 border-ink-200 hover:bg-ink-100 flex-col items-start"
        data-testid="auto-import-no"
      >
        <div className="flex items-center gap-3 w-full">
          <Palette className="w-6 h-6 text-ink-500" />
          <div className="text-left">
            <p className="font-semibold text-ink-950">Manual Entry</p>
            <p className="text-sm text-ink-500 font-normal">
              I&apos;ll add my products and services manually
            </p>
          </div>
        </div>
      </Button>
    </div>
  </div>
);

const StepDots = ({ count, current }) => (
  <div className="flex items-center gap-1.5">
    {Array.from({ length: count }).map((_, i) => (
      <div
        key={i}
        className={`h-1.5 rounded-full transition-all ${
          i === current ? "w-6 bg-ink-950" : "w-1.5 bg-ink-200"
        }`}
      />
    ))}
  </div>
);

export default OnboardingPopup;
